import React, { useState } from 'react';
import { motion } from "framer-motion"

import { useAppContext } from './AppContext';
import { Onboarding } from './Onboarding';
import OnboardingIndicators from './OnboardingIndicators';

import LoadingSpinner from './LoadingSpinner';
import useClinicCalendar from './hooks/useClinicCalendar';
import { logEvent } from 'firebase/analytics';
import { GoogleAnalytics } from './App';
import RoundedContainer from './layouts/RoundedContainer';
import Month, { generateEvents } from './components/Month';
import { addMonths, startOfMonth } from 'date-fns';
import { ClinicBlockAvailabilityForDates } from './common/ParseEventsToAvailability';

export default function HomeAuthenticated(props: any) {
  const app = useAppContext();
  const [demoAvailabilities] = useState<ClinicBlockAvailabilityForDates>(generateEvents());

 const [shouldLogOnboardedComplete,setShouldLogOnboardedComplete] = useState(false)

 
 function logTutorialStart() {
  if (!shouldLogOnboardedComplete) {
    logEvent(GoogleAnalytics, "tutorial_begin"); setShouldLogOnboardedComplete(true);
  }
 }

 function logTutorialEndIfIndicated(){
  if(shouldLogOnboardedComplete) {
    logEvent(GoogleAnalytics, "tutorial_complete")
  }
 }

  const {
    isOnboarded,
    iscalendarAccessable,
    isEpicScheduleSlotsAccessable,
    isEpicRoundsAccessable,
    isEpicAppointmentsAccessable,
  } = useClinicCalendar(true);


  const loadingView = <>
    <div className="tw-flex tw-flex-row tw-space-x-2 tw-items-center">
      <div className="tw-relative tw-h-5 tw-w-5">
        <LoadingSpinner />
      </div>
      <div>Checking Epic Integration</div>
    </div>
  </>;

  const onboardingView = <>

 
    <React.Fragment>
      
      <OnboardingIndicators
        calendarAccessable={iscalendarAccessable} 
        EpicScheduleSlotsAccessable={isEpicScheduleSlotsAccessable} 
        EpicRoundsAccessable={isEpicRoundsAccessable} 
        EpicAppointmentsAccessable={isEpicAppointmentsAccessable}/>
      <br></br>
      
      <div className="tw-flex tw-items-start tw-flex-col md:tw-flex-row tw-overflow-auto md:tw-overflow-visible tw-relative md:tw-space-x-10 md:tw-space-y-0">
        <div className="tw-space-x-10 tw-space-y-0 md:tw-space-x-0 md:tw-space-y-10 tw-flex tw-overflow-x-auto md:tw-flex-col tw-snap-x tw-w-full tw-pb-5 tw-flex-1 tw-snap-mandatory">
        </div>
      </div>
    </React.Fragment> 
    
    <Onboarding />
    
  </>;

  const isMobile = window.innerWidth < 768;
  const framerMotionVariants = isMobile ? undefined : {
    initial: {opacity: 0.5, scale: 0.8},
    whileInView: {opacity: 1, scale: 1},
  }

  const onboardedView = <>
    {/* snap scrolling causes a bug https://github.com/framer/motion/issues/342 */}
    <div className="tw-flex tw-flex-col md:tw-flex-row tw-flex-nowrap tw-space-y-3 md:tw-space-y-0 md:tw-space-x-20 tw-mb-3 md:tw-mb-10 tw-overflow-x-auto tw-w-full tw-snap-mandatory md:tw-absolute md:tw-left-0 md:tw-right-0 md:tw-py-10 md:tw-px-[20%] invisible-scrollbar">
      <motion.div
        variants={framerMotionVariants}
        initial="initial"
        whileInView="whileInView"
        transition={{ duration: 0.3 }}
        viewport={{ amount: "all" }}
        className="tw-bg-gradient-to-br tw-from-rose-200 tw-to-blue-300 tw-p-10 tw-rounded-2xl tw-w-full md:tw-w-[700px] tw-max-w-screen-md tw-shadow-md tw-shadow-black/5 tw-flex tw-flex-col tw-justify-between tw-flex-none tw-snap-center">
        <div>
          <h2 className="tw-font-bold tw-text-lg tw-text-blue-900">Share Your Calendar</h2>
          <p className="tw-mb-2 tw-text-blue-900">Great! Everything is set up with Epic! You can share your calendar publicly by enabling the public URL. If you want to stop sharing it, you can easily remove public access.</p>
        </div>

        <a href="/send-availability" className="tw-bg-blue-500 tw-text-white tw-w-full tw-text-center tw-py-2 tw-block tw-rounded-lg hover:tw-bg-blue-600 tw-text-sm">Get Started</a>
      </motion.div>

      <motion.div
        variants={framerMotionVariants}
        initial="initial"
        whileInView="whileInView"
        transition={{ duration: 0.3 }}
        viewport={{ amount: "all" }}
        className="tw-bg-gradient-to-tr tw-from-purple-200 tw-to-orange-300 tw-p-10 tw-rounded-2xl tw-w-full md:tw-w-[700px] tw-max-w-screen-md tw-shadow-md tw-shadow-black/5 tw-flex tw-flex-col tw-justify-between tw-flex-none tw-snap-center">
        <div>
          <h2 className="tw-font-bold tw-text-lg tw-text-sky-900">Show Your Availability</h2>
          <p className="tw-mb-2 tw-text-sky-900">Each day is split into your two clinic splots and your availability can be either green (available), yellow (split appointment), or red (no availability). Blue indicates it is a week you are assigned for hygiene checks. Availability is automatically synced with Epic in real-time.</p>

          <div className="tw-flex tw-flex-row tw-justify-center">
            <Month
              isDoingFirstLoad={false}
              availabilityInfoForEachDay={demoAvailabilities}
              monthStartDate={startOfMonth(addMonths(new Date(), 1))}
              className="tw-w-[500px] tw-h-[200px] tw-gradient-mask-b-70 tw-pointer-events-none" />
          </div> 
        </div>
      </motion.div>

      <motion.div
        variants={framerMotionVariants}
        initial="initial"
        whileInView="whileInView"
        transition={{ duration: 0.3 }}
        viewport={{ amount: "all" }}
        className="tw-bg-gradient-to-tr tw-from-rose-200 tw-to-blue-300 tw-p-10 tw-rounded-2xl tw-w-full md:tw-w-[700px] tw-max-w-screen-md tw-shadow-md tw-shadow-black/5 tw-flex tw-flex-col tw-justify-between tw-flex-none tw-snap-center">
        <div>
          <h2 className="tw-font-bold tw-text-lg tw-text-rose-800">Block Out Times</h2>
          <p className="tw-mb-2 tw-text-rose-800">Need to block or hide a time? Create an event in Outlook that overlaps with the relevant clinic slots. This will turn the corresponding clinic block red on your calendar</p>
        </div>

        <a href="https://outlook.office.com/calendar/view/week" className="tw-bg-rose-500 tw-text-white tw-w-full tw-text-center tw-py-2 tw-block tw-rounded-lg hover:tw-bg-rose-600 tw-text-sm">Block out</a>
      </motion.div>

      <motion.video
        variants={framerMotionVariants}
        initial="initial"
        whileInView="whileInView"
        transition={{ duration: 0.3 }}
        viewport={{ amount: "all" }}
        src="/images/IntentionalOutlookConflict.mp4" autoPlay muted controls loop className="tw-rounded-2xl tw-max-w-screen-md tw-shadow-md tw-flex-none tw-snap-center">
      </motion.video>
    </div>
  </>;

  function loadView(): React.ReactNode {
    if (app.events === undefined) {
      return loadingView;
    } else if (isOnboarded) {
      logTutorialEndIfIndicated()
      return onboardedView;
    }
    logTutorialStart()
    return onboardingView;
  }

  return (
    <RoundedContainer title="Getting Started" subheading={`${!isOnboarded ? "All the data sources used to schedule efficently, and how to enable them" : ""}`}>
      {loadView()}
    </RoundedContainer>
  );
}
